import { observe } from "../../../lib/reactive";
import Behaviour from "../../../lib/ui/Behaviour";
import { lazy } from "../../../lib/Lazy";
import { Alt1GLCapturedFrame } from "../../../lib/alt1/alt1gl/Alt1GLCapturedFrame";
import { getUniformValue } from "../../../lib/alt1/alt1gllib/ts/render/renderprogram";
import { Alt1 } from "../../../lib/alt1/Alt1";
import { Vector2 } from "../../../lib/math";
import { FakeLodash } from "../../../lib/coreutil/FakeLodash";
export var TrackedPlayerPosition;
(function (TrackedPlayerPosition) {
    function fromPosition(pos) {
        if (!pos)
            return null;
        return {
            position_3d: pos,
            tile: Vector2.snap({ x: pos.x, y: pos.z })
        };
    }
    TrackedPlayerPosition.fromPosition = fromPosition;
})(TrackedPlayerPosition || (TrackedPlayerPosition = {}));
export class PlayerStateTracking extends Behaviour {
    constructor() {
        super();
        this.framed_state = observe(null).structuralEquality();
        this.state = this.framed_state.map(v => v?.value).structuralEquality();
        this.state.subscribe(v => {
            //console.log(`Pos ${v.position.x.toFixed(2)}|${v.position.y.toFixed(2)}|${v.position.z.toFixed(2)}, Pulse ${v.pulse_type}`)
        });
    }
    begin() {
        if (Alt1.instance().featureGL()) {
            console.log("Starting player state tracking");
            this.stream = Alt1GLCapturedFrame.subscribe({
                features: [/*"vertexarray", */ "uniforms"],
                framecooldown: 600,
            }, (frame) => {
                this.framed_state.set(PlayerStateTracking.parsePlayerState(frame));
            });
            this.lifetime_manager.bind(this.stream);
        }
        else {
            console.log("Alt1GL not available, skipping player state tracking");
        }
    }
    end() {
        console.log("Stopping player state tracking");
        this.stream?.endLifetime();
        this.stream = null;
    }
    static instance() {
        return PlayerStateTracking._instance.get();
    }
}
PlayerStateTracking._instance = lazy(() => new PlayerStateTracking().start());
(function (PlayerStateTracking) {
    function parsePlayerState(frame) {
        const position = PlayerStateTracking.detectPlayerPosition(frame);
        const scan_pulse = PlayerStateTracking.detectPulseRing(frame, position);
        const compass = null; // CapturedCompassGl.findCompass(frame)
        return {
            frame: frame.frame_number,
            value: {
                position: TrackedPlayerPosition.fromPosition(position),
                pulse_type: scan_pulse,
                compass_angle: compass?.getAngle()
            }
        };
    }
    PlayerStateTracking.parsePlayerState = parsePlayerState;
    function detectPlayerPosition(frame) {
        const TILESIZE = 512;
        for (let render of frame.renders) {
            if (!render.progmeta.get().isTinted || !render.progmeta.get().uTint || !render.progmeta.get().uModelMatrix || !render.progmeta.get().isAnimated)
                continue;
            if (typeof render.raw.program.fragmentShader?.source !== 'string')
                continue;
            if (typeof render.raw.program.vertexShader?.source !== 'string')
                continue;
            try {
                const tint = getUniformValue(render.raw.uniformState, render.progmeta.get().uTint)[0];
                if (!tint || tint.length < 4)
                    continue;
                if (Math.abs(tint[0]) + Math.abs(tint[1]) + Math.abs(tint[2]) > 0.1 || tint[3] > 0.6)
                    continue;
                const m = getUniformValue(render.raw.uniformState, render.progmeta.get().uModelMatrix)[0];
                if (!m || m.length < 16)
                    continue;
                const x = m[12] / TILESIZE - 0.5, y = m[13] / TILESIZE, z = m[14] / TILESIZE - 0.5;
                return (x === 0 && z === 0) ? null : { x, y, z };
            }
            catch { }
        }
        return null;
    }
    PlayerStateTracking.detectPlayerPosition = detectPlayerPosition;
    function toTile(worldCoord) {
        return Math.floor(worldCoord / 512 + 1e-3);
    }
    /**
     * Detects the current pulse ring from a captured frame.
     * @param frame The frame to search in.
     * @param position The already known player position. Required for filtering false positives.
     */
    function detectPulseRing(frame, position) {
        if (!position)
            return null;
        for (let render of frame.renders) {
            if (!render.progmeta.get().isAnimated || !render.progmeta.get().isMainMesh)
                continue;
            try {
                if (!render.raw.renderRanges)
                    continue;
                const verts = FakeLodash.sumBy(render.raw.renderRanges, r => r.length);
                const m = getUniformValue(render.raw.uniformState, render.progmeta.get().uModelMatrix)[0];
                if (toTile(m[12]) !== position.x || toTile(m[14]) !== position.z)
                    continue;
                if (verts === 576)
                    return 1;
                if (verts === 864)
                    return 2;
                if (verts === 1728)
                    return 3;
            }
            catch { }
        }
        return null;
    }
    PlayerStateTracking.detectPulseRing = detectPulseRing;
})(PlayerStateTracking || (PlayerStateTracking = {}));
